import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  X,
  Mail,
  CheckCircle,
  MessageSquare,
  Calendar,
  ChevronDown,
  ChevronRight,
  List,
  LayoutGrid,
  Download,
  FileSpreadsheet,
  FileText,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';
import ExcelJS from 'exceljs';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import api from '../../../services/api';
import {
  getPreloadState,
  preloadStates,
  formatDate,
  formatDateShort,
  formatGap,
  formatThreshold,
  DAY_RANGES,
} from './preloadConfig';
import { toApiDateString } from '../../../utils/dateUtils';
import PreloadAcknowledgeModal from './PreloadAcknowledgeModal';
import PreloadAlertLogModal from './PreloadAlertLogModal';
import { useThemeTokens } from '../../../utils/themeTokens';

const ChartTooltip = ({ active, payload, alertThreshold }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <div className="bg-surface border border-gray-200 rounded-md px-3 py-2 shadow-lg text-[11px]">
      <div className="text-slate-800 font-medium mb-1">{formatDate(d.date)}</div>
      <div className="flex items-center gap-2">
        <span
          className={`font-bold ${d.preloadCount >= alertThreshold ? 'text-red-600' : d.preloadCount > 0 ? 'text-amber-600' : 'text-primary-500'}`}
        >
          {d.preloadCount} preload{d.preloadCount !== 1 ? 's' : ''}
        </span>
        <span className="text-gray-300">|</span>
        <span className="text-gray-400">{d.totalTickets} total</span>
      </div>
    </div>
  );
};

const PreloadDetailModal = ({ monitor, onClose, onRefresh }) => {
  const t = useThemeTokens();
  const [results, setResults] = useState([]);
  const [summary, setSummary] = useState(null);
  const [myAcks, setMyAcks] = useState({});
  const [allAcks, setAllAcks] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAckModal, setShowAckModal] = useState(null);
  const [showAlertLog, setShowAlertLog] = useState(false);
  const [dayRange, setDayRange] = useState(7);
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [ticketData, setTicketData] = useState({});
  const [viewMode, setViewMode] = useState('daily'); // 'daily' or 'all-preloads'
  const [allPreloads, setAllPreloads] = useState(null);
  const [loadingAllPreloads, setLoadingAllPreloads] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [r, s, a] = await Promise.all([
        api.preloadEvaluation.getResults(monitor.monitor_id, monitor.lookback_days || 40),
        api.preloadEvaluation.getResultSummary(monitor.monitor_id),
        api.preloadEvaluation.getMyAcknowledgments(monitor.monitor_id),
      ]);
      setResults(r.data || []);
      setSummary(s.data || {});
      setMyAcks(a.data?.ackMap || {});
    } catch {
    } finally {
      setLoading(false);
    }
  }, [monitor.monitor_id, monitor.lookback_days]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    const fn = async () => {
      const withPreloads = results.filter((r) => r.preload_count > 0);
      const data = {};
      await Promise.all(
        withPreloads.map(async (r) => {
          try {
            const res = await api.preloadEvaluation.getAcknowledgments(r.result_id);
            data[r.result_id] = res.data || [];
          } catch {
            data[r.result_id] = [];
          }
        })
      );
      setAllAcks(data);
    };
    if (results.length > 0) fn();
  }, [results]);

  // Fetch all preloads when switching to that view
  useEffect(() => {
    if (viewMode === 'all-preloads' && allPreloads === null && !loadingAllPreloads) {
      setLoadingAllPreloads(true);
      api.preloadEvaluation
        .getMonitorTickets(monitor.monitor_id, monitor.lookback_days || 40)
        .then((res) => setAllPreloads(res.data || []))
        .catch(() => setAllPreloads([]))
        .finally(() => setLoadingAllPreloads(false));
    }
  }, [viewMode, allPreloads, loadingAllPreloads, monitor.monitor_id, monitor.lookback_days]);

  const handleAckSaved = () => {
    setShowAckModal(null);
    fetchData();
    onRefresh();
  };

  const toggleRow = async (resultId) => {
    const next = new Set(expandedRows);
    if (next.has(resultId)) {
      next.delete(resultId);
    } else {
      next.add(resultId);
      if (!ticketData[resultId]) {
        try {
          const res = await api.preloadEvaluation.getTickets(resultId);
          setTicketData((prev) => ({ ...prev, [resultId]: res.data || [] }));
        } catch {
          setTicketData((prev) => ({ ...prev, [resultId]: [] }));
        }
      }
    }
    setExpandedRows(next);
  };

  const filteredResults = useMemo(() => {
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - dayRange);
    return results.filter((r) => new Date(r.result_date) >= cutoff);
  }, [results, dayRange]);

  const filteredAllPreloads = useMemo(() => {
    if (!allPreloads) return [];
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - dayRange);
    return allPreloads.filter((t) => new Date(t.result_date) >= cutoff);
  }, [allPreloads, dayRange]);

  const chartData = useMemo(() => {
    return [...filteredResults]
      .filter((r) => r.total_tickets > 0)
      .sort((a, b) => new Date(a.result_date) - new Date(b.result_date))
      .map((r) => ({
        date: r.result_date,
        dateLabel: formatDateShort(r.result_date),
        preloadCount: r.preload_count,
        totalTickets: r.total_tickets,
      }));
  }, [filteredResults]);

  const alertThreshold = monitor.alert_threshold;
  const latestPreloads = monitor.latest_preload_count || 0;
  const state = getPreloadState(latestPreloads, alertThreshold);
  const preloadDaysInRange = filteredResults.filter((r) => r.preload_count > 0).length;
  const dataInRange = filteredResults.filter((r) => r.total_tickets > 0).length;

  const yMax = useMemo(() => {
    if (!chartData.length) return 5;
    const maxVal = Math.max(...chartData.map((d) => d.preloadCount), alertThreshold);
    return Math.ceil(maxVal * 1.3) || 5;
  }, [chartData, alertThreshold]);

  // Ensure preload data is loaded for export
  const ensurePreloadData = async () => {
    if (allPreloads) return allPreloads;
    const res = await api.preloadEvaluation.getMonitorTickets(
      monitor.monitor_id,
      monitor.lookback_days || 40
    );
    const data = res.data || [];
    setAllPreloads(data);
    return data;
  };

  const getExportData = async () => {
    const tickets = await ensurePreloadData();
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - dayRange);
    return tickets.filter((t) => new Date(t.result_date) >= cutoff);
  };

  const fmtTs = (ts) =>
    new Date(ts).toLocaleString('en-ZA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  const fmtWt = (w) => (w ? parseFloat(w).toLocaleString('en', { maximumFractionDigits: 1 }) : '-');

  const exportToExcel = async () => {
    setExporting(true);
    setShowExportMenu(false);
    try {
      const data = await getExportData();
      const wb = new ExcelJS.Workbook();
      wb.creator = 'LexiBridge';
      wb.created = new Date();

      // -- Sheet 1: Daily Summary --
      const ws1 = wb.addWorksheet('Daily Summary');
      const teal = { argb: 'FF45B7AA' };
      const charcoal = { argb: 'FF2D3142' };
      const red = { argb: 'FFDC2626' };
      const lightGray = { argb: 'FFF9FAFB' };
      const white = { argb: 'FFFFFFFF' };

      // Title block
      ws1.mergeCells('A1:F1');
      const titleCell = ws1.getCell('A1');
      titleCell.value = `Preload Evaluation Report`;
      titleCell.font = { bold: true, size: 16, color: charcoal };
      ws1.getRow(1).height = 28;

      ws1.mergeCells('A2:F2');
      const subtitleCell = ws1.getCell('A2');
      subtitleCell.value = monitor.grouping_name;
      subtitleCell.font = { bold: true, size: 12, color: teal };
      ws1.getRow(2).height = 20;

      ws1.mergeCells('A3:F3');
      ws1.getCell('A3').value =
        `Time Threshold: ${formatThreshold(monitor.time_threshold_minutes)}  |  Alert At: ${alertThreshold} preload(s)  |  Range: ${dayRange} days  |  Generated: ${new Date().toLocaleString('en-ZA')}`;
      ws1.getCell('A3').font = { size: 9, color: { argb: 'FF9CA3AF' } };
      ws1.getRow(3).height = 18;

      // Blank row
      ws1.getRow(4).height = 8;

      // Summary headers
      const s1Headers = ['Date', 'Total Tickets', 'Preloads', 'Status'];
      const s1HeaderRow = ws1.addRow(s1Headers);
      s1HeaderRow.height = 22;
      s1HeaderRow.eachCell((cell, col) => {
        cell.font = { bold: true, size: 9, color: white };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: charcoal };
        cell.alignment = { vertical: 'middle', horizontal: col >= 2 ? 'center' : 'left' };
        cell.border = { bottom: { style: 'thin', color: { argb: 'FFE5E7EB' } } };
      });

      const daysWithData = filteredResults.filter((r) => r.total_tickets > 0);
      daysWithData.sort((a, b) => new Date(b.result_date) - new Date(a.result_date));
      daysWithData.forEach((r, i) => {
        const status =
          r.preload_count >= alertThreshold ? 'ALERT' : r.preload_count > 0 ? 'WATCH' : 'CLEAR';
        const row = ws1.addRow([
          formatDate(r.result_date),
          r.total_tickets,
          r.preload_count,
          status,
        ]);
        row.height = 18;
        const bg = i % 2 === 0 ? white : lightGray;
        row.eachCell((cell, col) => {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: bg };
          cell.alignment = { vertical: 'middle', horizontal: col >= 2 ? 'center' : 'left' };
          cell.font = { size: 9, color: charcoal };
          cell.border = { bottom: { style: 'hair', color: { argb: 'FFE5E7EB' } } };
        });
        // Color the preload count
        if (r.preload_count > 0) {
          const plCell = row.getCell(3);
          plCell.font = {
            bold: true,
            size: 9,
            color: r.preload_count >= alertThreshold ? red : { argb: 'FFD97706' },
          };
        }
        // Color the status
        const stCell = row.getCell(4);
        if (status === 'ALERT') stCell.font = { bold: true, size: 9, color: red };
        else if (status === 'WATCH')
          stCell.font = { bold: true, size: 9, color: { argb: 'FFD97706' } };
        else stCell.font = { size: 9, color: teal };
      });

      ws1.columns = [{ width: 18 }, { width: 16 }, { width: 14 }, { width: 12 }];

      // -- Sheet 2: All Preloads --
      if (data.length > 0) {
        const ws2 = wb.addWorksheet('Preload Tickets');

        ws2.mergeCells('A1:I1');
        const t2 = ws2.getCell('A1');
        t2.value = `Preload Tickets - ${monitor.grouping_name}`;
        t2.font = { bold: true, size: 14, color: charcoal };
        ws2.getRow(1).height = 26;

        ws2.mergeCells('A2:I2');
        ws2.getCell('A2').value =
          `${data.length} preload(s) found  |  ${dayRange}-day range  |  Threshold: ${formatThreshold(monitor.time_threshold_minutes)}`;
        ws2.getCell('A2').font = { size: 9, color: { argb: 'FF9CA3AF' } };
        ws2.getRow(2).height = 18;
        ws2.getRow(3).height = 6;

        const headers = [
          'Receipt Date',
          'Delivery No',
          'Reg No',
          'Dispatched',
          'Received',
          'Gap',
          'Product',
          'Dispatch (t)',
          'Receipt (t)',
        ];
        const hRow = ws2.addRow(headers);
        hRow.height = 22;
        hRow.eachCell((cell, col) => {
          cell.font = { bold: true, size: 9, color: white };
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF991B1B' } };
          cell.alignment = { vertical: 'middle', horizontal: col >= 6 ? 'right' : 'left' };
          cell.border = { bottom: { style: 'thin', color: { argb: 'FFDC2626' } } };
        });

        data.forEach((t, i) => {
          const row = ws2.addRow([
            formatDate(t.result_date),
            t.delivery_no,
            t.reg_no || '-',
            fmtTs(t.dispatch_timestamp),
            fmtTs(t.receipt_timestamp),
            formatGap(t.gap_minutes),
            t.product || '-',
            t.dispatch_weight ? parseFloat(t.dispatch_weight).toFixed(1) : '-',
            t.receipt_weight ? parseFloat(t.receipt_weight).toFixed(1) : '-',
          ]);
          row.height = 18;
          const bg = i % 2 === 0 ? white : { argb: 'FFFFF5F5' };
          row.eachCell((cell, col) => {
            cell.fill = { type: 'pattern', pattern: 'solid', fgColor: bg };
            cell.alignment = { vertical: 'middle', horizontal: col >= 6 ? 'right' : 'left' };
            cell.font = { size: 9, color: charcoal };
            cell.border = { bottom: { style: 'hair', color: { argb: 'FFFECACA' } } };
          });
          // Bold red gap
          const gapCell = row.getCell(6);
          gapCell.font = { bold: true, size: 9, color: red };
        });

        ws2.columns = [
          { width: 16 },
          { width: 18 },
          { width: 14 },
          { width: 22 },
          { width: 22 },
          { width: 12 },
          { width: 20 },
          { width: 14 },
          { width: 14 },
        ];
      }

      const buffer = await wb.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `preload-report-${monitor.grouping_name.replace(/[^a-zA-Z0-9]/g, '_')}-${toApiDateString(new Date())}.xlsx`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Excel export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  const exportToPDF = async () => {
    setExporting(true);
    setShowExportMenu(false);
    try {
      const data = await getExportData();
      const doc = new jsPDF({ orientation: data.length > 5 ? 'landscape' : 'portrait' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const margin = 12;
      let y = 12;

      // Title
      doc.setFontSize(18);
      doc.setFont(undefined, 'bold');
      doc.setTextColor(45, 49, 66); // charcoal
      doc.text('Preload Evaluation Report', margin, y + 6);
      y += 14;

      // Subtitle
      doc.setFontSize(12);
      doc.setFont(undefined, 'normal');
      doc.setTextColor(69, 183, 170); // teal
      doc.text(monitor.grouping_name, margin, y);
      y += 8;

      // Meta line
      doc.setFontSize(8);
      doc.setTextColor(156, 163, 175);
      doc.text(
        `Time Threshold: ${formatThreshold(monitor.time_threshold_minutes)}  |  Alert At: ${alertThreshold}  |  Range: ${dayRange} days  |  Generated: ${new Date().toLocaleString('en-ZA')}`,
        margin,
        y
      );
      y += 10;

      // KPI boxes
      const boxWidth = (pageWidth - margin * 2 - 12) / 4;
      const kpis = [
        {
          label: 'LATEST',
          value: `${latestPreloads}`,
          color: latestPreloads >= alertThreshold ? [220, 38, 38] : [69, 183, 170],
        },
        { label: 'ALERT AT', value: `${alertThreshold}`, color: [45, 49, 66] },
        {
          label: 'DAYS W/ PRELOADS',
          value: `${preloadDaysInRange} / ${dataInRange}`,
          color: preloadDaysInRange > 0 ? [220, 38, 38] : [45, 49, 66],
        },
        {
          label: 'TOTAL PRELOADS',
          value: `${data.length}`,
          color: data.length > 0 ? [220, 38, 38] : [45, 49, 66],
        },
      ];
      kpis.forEach((kpi, i) => {
        const x = margin + i * (boxWidth + 4);
        doc.setFillColor(249, 250, 251);
        doc.setDrawColor(229, 231, 235);
        doc.roundedRect(x, y, boxWidth, 16, 2, 2, 'FD');
        doc.setFontSize(12);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(...kpi.color);
        doc.text(kpi.value, x + boxWidth / 2, y + 7, { align: 'center' });
        doc.setFontSize(6);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(156, 163, 175);
        doc.text(kpi.label, x + boxWidth / 2, y + 12.5, { align: 'center' });
      });
      y += 22;

      // Daily summary table
      const dailyData = filteredResults
        .filter((r) => r.total_tickets > 0)
        .sort((a, b) => new Date(b.result_date) - new Date(a.result_date))
        .map((r) => [
          formatDate(r.result_date),
          String(r.total_tickets),
          String(r.preload_count),
          r.preload_count >= alertThreshold ? 'ALERT' : r.preload_count > 0 ? 'WATCH' : 'CLEAR',
        ]);

      if (dailyData.length > 0) {
        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(45, 49, 66);
        doc.text('Daily Summary', margin, y);
        y += 4;

        doc.autoTable({
          head: [['Date', 'Total Tickets', 'Preloads', 'Status']],
          body: dailyData,
          startY: y,
          margin: { left: margin, right: margin },
          theme: 'plain',
          styles: {
            fontSize: 7.5,
            cellPadding: { top: 2, right: 4, bottom: 2, left: 4 },
            lineColor: [229, 231, 235],
            lineWidth: 0.2,
          },
          headStyles: {
            fillColor: [45, 49, 66],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 7.5,
          },
          alternateRowStyles: { fillColor: [249, 250, 251] },
          columnStyles: {
            0: { halign: 'left', cellWidth: 30 },
            1: { halign: 'center', cellWidth: 24 },
            2: { halign: 'center', cellWidth: 20 },
            3: { halign: 'center', cellWidth: 18 },
          },
          didParseCell: (hookData) => {
            if (hookData.section === 'body' && hookData.column.index === 2) {
              const val = parseInt(hookData.cell.raw);
              if (val >= alertThreshold) {
                hookData.cell.styles.textColor = [220, 38, 38];
                hookData.cell.styles.fontStyle = 'bold';
              } else if (val > 0) {
                hookData.cell.styles.textColor = [217, 119, 6];
                hookData.cell.styles.fontStyle = 'bold';
              }
            }
            if (hookData.section === 'body' && hookData.column.index === 3) {
              if (hookData.cell.raw === 'ALERT') {
                hookData.cell.styles.textColor = [220, 38, 38];
                hookData.cell.styles.fontStyle = 'bold';
              } else if (hookData.cell.raw === 'WATCH') {
                hookData.cell.styles.textColor = [217, 119, 6];
                hookData.cell.styles.fontStyle = 'bold';
              } else {
                hookData.cell.styles.textColor = [69, 183, 170];
              }
            }
          },
        });
        y = doc.lastAutoTable.finalY + 10;
      }

      // Preload tickets table
      if (data.length > 0) {
        // Check if we need a new page
        if (y > doc.internal.pageSize.getHeight() - 40) {
          doc.addPage();
          y = 12;
        }

        doc.setFontSize(10);
        doc.setFont(undefined, 'bold');
        doc.setTextColor(153, 27, 27);
        doc.text(`Preload Tickets (${data.length})`, margin, y);
        y += 4;

        const ticketRows = data.map((t) => [
          formatDate(t.result_date),
          t.delivery_no,
          t.reg_no || '-',
          fmtTs(t.dispatch_timestamp),
          fmtTs(t.receipt_timestamp),
          formatGap(t.gap_minutes),
          (t.product || '-').substring(0, 20),
          t.dispatch_weight ? parseFloat(t.dispatch_weight).toFixed(1) : '-',
          t.receipt_weight ? parseFloat(t.receipt_weight).toFixed(1) : '-',
        ]);

        doc.autoTable({
          head: [
            [
              'Date',
              'Delivery No',
              'Reg No',
              'Dispatched',
              'Received',
              'Gap',
              'Product',
              'D. (t)',
              'R. (t)',
            ],
          ],
          body: ticketRows,
          startY: y,
          margin: { left: margin, right: margin },
          theme: 'plain',
          styles: {
            fontSize: 7,
            cellPadding: { top: 1.5, right: 3, bottom: 1.5, left: 3 },
            lineColor: [254, 202, 202],
            lineWidth: 0.15,
          },
          headStyles: {
            fillColor: [153, 27, 27],
            textColor: [255, 255, 255],
            fontStyle: 'bold',
            fontSize: 7,
          },
          alternateRowStyles: { fillColor: [255, 245, 245] },
          columnStyles: {
            5: { fontStyle: 'bold', textColor: [220, 38, 38], halign: 'right' },
            7: { halign: 'right' },
            8: { halign: 'right' },
          },
        });
      }

      // Footer
      const pageCount = doc.internal.getNumberOfPages();
      for (let p = 1; p <= pageCount; p++) {
        doc.setPage(p);
        doc.setFontSize(7);
        doc.setFont(undefined, 'normal');
        doc.setTextColor(156, 163, 175);
        doc.text(
          `LexiBridge Preload Report - Page ${p} of ${pageCount}`,
          pageWidth / 2,
          doc.internal.pageSize.getHeight() - 6,
          { align: 'center' }
        );
      }

      doc.save(
        `preload-report-${monitor.grouping_name.replace(/[^a-zA-Z0-9]/g, '_')}-${toApiDateString(new Date())}.pdf`
      );
    } catch (err) {
      console.error('PDF export failed:', err);
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-surface rounded-lg border border-gray-200 w-full max-w-5xl max-h-[92vh] flex flex-col shadow-xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 flex-shrink-0">
          <div>
            <h2 className="text-sm font-semibold text-slate-800">{monitor.grouping_name}</h2>
            <p className="text-[11px] text-gray-400 mt-0.5">
              Preload evaluation &middot; Threshold{' '}
              {formatThreshold(monitor.time_threshold_minutes)}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowAlertLog(true)}
              className="inline-flex items-center gap-1 h-7 px-2.5 text-[11px] text-gray-500 hover:text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md transition-colors"
            >
              <Mail className="w-3 h-3" /> Alert Log
            </button>
            <button onClick={onClose} className="p-1 rounded hover:bg-gray-100">
              <X className="w-4 h-4 text-gray-400" />
            </button>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid grid-cols-4 gap-px bg-gray-100 border-b border-gray-100 flex-shrink-0">
          {[
            {
              label: 'Latest',
              value: `${latestPreloads} preload${latestPreloads !== 1 ? 's' : ''}`,
              color: state.accent,
            },
            { label: 'Alert At', value: `${alertThreshold}`, color: 'var(--text-strong)' },
            {
              label: 'Days w/ Preloads',
              value: `${preloadDaysInRange} / ${dataInRange}`,
              color: preloadDaysInRange > 0 ? 'var(--danger)' : 'var(--text-strong)',
            },
            {
              label: 'Last Alert',
              value: summary?.lastAlert ? formatDate(summary.lastAlert.sent_at) : 'Never',
              color: 'var(--text-muted)',
            },
          ].map((k, i) => (
            <div key={i} className="bg-surface px-4 py-2.5 text-center">
              <div className="text-sm font-bold tabular-nums" style={{ color: k.color }}>
                {k.value}
              </div>
              <div className="text-[10px] text-gray-400 uppercase tracking-wider mt-0.5">
                {k.label}
              </div>
            </div>
          ))}
        </div>

        {/* Chart */}
        <div className="px-5 pt-4 pb-2 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-medium text-gray-500">Preload Trend</span>
            <div className="flex items-center gap-0.5 bg-gray-100 rounded-md p-0.5">
              {DAY_RANGES.map((r) => (
                <button
                  key={r.value}
                  onClick={() => setDayRange(r.value)}
                  className={`px-2.5 py-1 text-[10px] font-semibold rounded transition-all ${
                    dayRange === r.value
                      ? 'bg-surface text-slate-800 shadow-sm'
                      : 'text-gray-400 hover:text-gray-600'
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>
          </div>
          {loading ? (
            <div className="h-48 bg-gray-50 animate-pulse rounded" />
          ) : chartData.length === 0 ? (
            <div className="h-48 flex items-center justify-center text-xs text-gray-300">
              No data for selected range
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={chartData} margin={{ top: 8, right: 12, left: -15, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={t.gridSubtle} vertical={false} />
                <XAxis
                  dataKey="dateLabel"
                  tick={{ fontSize: 10, fill: t.axisLabel }}
                  axisLine={{ stroke: t.grid }}
                  tickLine={false}
                />
                <YAxis
                  domain={[0, yMax]}
                  tick={{ fontSize: 10, fill: t.axisLabel }}
                  axisLine={false}
                  tickLine={false}
                  allowDecimals={false}
                />
                <Tooltip
                  content={<ChartTooltip alertThreshold={alertThreshold} />}
                  cursor={{ fill: t.cursor }}
                />
                {alertThreshold > 1 && (
                  <ReferenceLine
                    y={alertThreshold}
                    stroke={t.danger}
                    strokeDasharray="4 4"
                    strokeWidth={1}
                    strokeOpacity={0.5}
                    label={{
                      value: `Alert: ${alertThreshold}`,
                      position: 'right',
                      fontSize: 9,
                      fill: t.danger,
                    }}
                  />
                )}
                <Bar dataKey="preloadCount" radius={[3, 3, 0, 0]} maxBarSize={32}>
                  {chartData.map((entry, idx) => (
                    <Cell
                      key={idx}
                      fill={
                        entry.preloadCount >= alertThreshold
                          ? t.danger
                          : entry.preloadCount > 0
                            ? t.warning
                            : t.accent
                      }
                      fillOpacity={0.85}
                    />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        {/* View toggle + Export */}
        <div className="flex items-center justify-between px-5 py-2.5 border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center gap-1 bg-gray-100 rounded-md p-0.5">
            <button
              onClick={() => setViewMode('daily')}
              className={`inline-flex items-center gap-1.5 px-3 py-1 text-[10px] font-semibold rounded transition-all ${
                viewMode === 'daily'
                  ? 'bg-surface text-slate-800 shadow-sm'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <LayoutGrid className="w-3 h-3" /> Daily Summary
            </button>
            <button
              onClick={() => setViewMode('all-preloads')}
              className={`inline-flex items-center gap-1.5 px-3 py-1 text-[10px] font-semibold rounded transition-all ${
                viewMode === 'all-preloads'
                  ? 'bg-surface text-slate-800 shadow-sm'
                  : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <List className="w-3 h-3" /> All Preloads
              {filteredAllPreloads.length > 0 && (
                <span className="ml-0.5 px-1.5 py-0.5 bg-red-100 text-red-700 rounded-full text-[9px] font-bold">
                  {filteredAllPreloads.length}
                </span>
              )}
            </button>
          </div>
          <div className="relative">
            <button
              onClick={() => setShowExportMenu(!showExportMenu)}
              disabled={exporting}
              className="inline-flex items-center gap-1.5 h-7 px-2.5 text-[11px] font-medium text-gray-500 hover:text-gray-700 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-md transition-colors disabled:opacity-50"
            >
              <Download className={`w-3 h-3 ${exporting ? 'animate-spin' : ''}`} />
              {exporting ? 'Exporting...' : 'Export'}
            </button>
            {showExportMenu && (
              <>
                <div className="fixed inset-0 z-10" onClick={() => setShowExportMenu(false)} />
                <div className="absolute right-0 top-full mt-1 w-40 bg-surface rounded-lg shadow-lg border border-gray-200 z-20 py-1">
                  <button
                    onClick={exportToExcel}
                    className="w-full px-3 py-2 text-left text-xs text-gray-600 hover:bg-emerald-50 hover:text-emerald-700 flex items-center gap-2 transition-colors"
                  >
                    <FileSpreadsheet className="w-3.5 h-3.5 text-emerald-600" /> Export to Excel
                  </button>
                  <button
                    onClick={exportToPDF}
                    className="w-full px-3 py-2 text-left text-xs text-gray-600 hover:bg-red-50 hover:text-red-700 flex items-center gap-2 transition-colors"
                  >
                    <FileText className="w-3.5 h-3.5 text-red-600" /> Export to PDF
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Content area */}
        <div className="flex-1 overflow-auto">
          {viewMode === 'daily' ? (
            /* ============ DAILY SUMMARY VIEW ============ */
            loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-primary-500" />
              </div>
            ) : filteredResults.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-6 h-6 mx-auto mb-2 text-gray-200" />
                <p className="text-xs text-gray-400">No results. Run the monitor.</p>
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 bg-gray-50 z-10">
                  <tr className="border-b border-gray-200">
                    {['', 'Date', 'Total', 'Preloads', 'Status', 'Acknowledged', 'Action'].map(
                      (h, i) => (
                        <th
                          key={h}
                          className={`px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider ${i >= 2 && i <= 3 ? 'text-right' : i >= 4 ? 'text-center' : 'text-left'}`}
                        >
                          {h}
                        </th>
                      )
                    )}
                  </tr>
                </thead>
                <tbody>
                  {filteredResults.map((r) => {
                    const hasData = r.total_tickets > 0;
                    if (!hasData)
                      return (
                        <tr key={r.result_id} className="border-b border-gray-50">
                          <td className="px-3 py-1.5" />
                          <td className="px-3 py-1.5 text-gray-400">{formatDate(r.result_date)}</td>
                          <td
                            colSpan={5}
                            className="px-3 py-1.5 text-center text-gray-300 text-[10px] italic"
                          >
                            No data
                          </td>
                        </tr>
                      );
                    const rowState =
                      r.preload_count >= alertThreshold
                        ? preloadStates.alert
                        : r.preload_count > 0
                          ? preloadStates.watch
                          : preloadStates.normal;
                    const isAcked = !!myAcks[r.result_id];
                    const acks = allAcks[r.result_id] || [];
                    const isExpanded = expandedRows.has(r.result_id);
                    const tickets = ticketData[r.result_id];

                    return (
                      <React.Fragment key={r.result_id}>
                        <tr
                          className={`border-b border-gray-50 transition-colors ${r.preload_count > 0 ? rowState.rowBg : 'hover:bg-gray-50/50'} ${r.preload_count > 0 ? 'cursor-pointer' : ''}`}
                          onClick={() => r.preload_count > 0 && toggleRow(r.result_id)}
                        >
                          <td className="px-3 py-2 w-8">
                            {r.preload_count > 0 &&
                              (isExpanded ? (
                                <ChevronDown className="w-3.5 h-3.5 text-gray-400" />
                              ) : (
                                <ChevronRight className="w-3.5 h-3.5 text-gray-300" />
                              ))}
                          </td>
                          <td className="px-3 py-2">
                            <span className="text-slate-800 font-medium">
                              {formatDate(r.result_date)}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                            {r.total_tickets}
                          </td>
                          <td
                            className="px-3 py-2 text-right tabular-nums font-bold"
                            style={{ color: rowState.accent }}
                          >
                            {r.preload_count}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <span
                              className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-semibold ${rowState.badgeBg} ${rowState.badgeText} border ${rowState.badgeBorder}`}
                            >
                              {React.createElement(rowState.icon, { className: 'w-2.5 h-2.5' })}{' '}
                              {rowState.label}
                            </span>
                          </td>
                          <td
                            className="px-3 py-2 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            {r.preload_count > 0 && acks.length > 0 ? (
                              <div className="space-y-0.5">
                                {acks.map((a) => (
                                  <div
                                    key={a.acknowledgment_id}
                                    className="flex items-center justify-center gap-1 text-[10px]"
                                    title={a.note}
                                  >
                                    <CheckCircle className="w-2.5 h-2.5 text-primary-500" />
                                    <span className="text-gray-500 truncate max-w-[80px]">
                                      {a.user_name}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            ) : r.preload_count > 0 ? (
                              <span className="text-[10px] text-gray-300">Pending</span>
                            ) : null}
                          </td>
                          <td
                            className="px-3 py-2 text-center"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <div className="flex items-center justify-center gap-1">
                              {r.preload_count > 0 && !isAcked && (
                                <button
                                  onClick={() => setShowAckModal(r)}
                                  className="inline-flex items-center gap-0.5 px-2 py-0.5 text-[10px] font-medium text-primary-500 bg-teal-50 hover:bg-teal-100 border border-teal-200 rounded transition-colors"
                                >
                                  <MessageSquare className="w-2.5 h-2.5" /> Ack
                                </button>
                              )}
                              {r.preload_count > 0 && isAcked && (
                                <CheckCircle className="w-3.5 h-3.5 text-primary-500" />
                              )}
                              {r.alert_sent && (
                                <Mail className="w-3 h-3 text-amber-400" title="Alert sent" />
                              )}
                            </div>
                          </td>
                        </tr>
                        {isExpanded && (
                          <tr className="bg-gray-50/80">
                            <td colSpan={7} className="px-6 py-3">
                              {!tickets ? (
                                <div className="text-center py-2">
                                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-gray-200 border-t-primary-500 mx-auto" />
                                </div>
                              ) : tickets.length === 0 ? (
                                <p className="text-[10px] text-gray-400 text-center">
                                  No ticket details available.
                                </p>
                              ) : (
                                <table className="w-full text-[11px]">
                                  <thead>
                                    <tr className="text-[9px] font-semibold text-gray-400 uppercase tracking-wider">
                                      <th className="pb-1.5 text-left">Delivery No</th>
                                      <th className="pb-1.5 text-left">Reg No</th>
                                      <th className="pb-1.5 text-left">Dispatched</th>
                                      <th className="pb-1.5 text-left">Received</th>
                                      <th className="pb-1.5 text-right">Gap</th>
                                      <th className="pb-1.5 text-left">Product</th>
                                      <th className="pb-1.5 text-right">D. Weight</th>
                                      <th className="pb-1.5 text-right">R. Weight</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {tickets.map((t) => (
                                      <tr
                                        key={t.preload_ticket_id}
                                        className="border-t border-gray-200/50"
                                      >
                                        <td className="py-1.5 text-slate-800 font-medium">
                                          {t.delivery_no}
                                        </td>
                                        <td className="py-1.5 text-gray-500">{t.reg_no || '-'}</td>
                                        <td className="py-1.5 text-gray-500">
                                          {new Date(t.dispatch_timestamp).toLocaleString('en-ZA', {
                                            day: '2-digit',
                                            month: 'short',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          })}
                                        </td>
                                        <td className="py-1.5 text-gray-500">
                                          {new Date(t.receipt_timestamp).toLocaleString('en-ZA', {
                                            day: '2-digit',
                                            month: 'short',
                                            hour: '2-digit',
                                            minute: '2-digit',
                                          })}
                                        </td>
                                        <td className="py-1.5 text-right font-bold text-red-600">
                                          {formatGap(t.gap_minutes)}
                                        </td>
                                        <td className="py-1.5 text-gray-400 truncate max-w-[100px]">
                                          {t.product || '-'}
                                        </td>
                                        <td className="py-1.5 text-right tabular-nums text-gray-500">
                                          {t.dispatch_weight
                                            ? `${parseFloat(t.dispatch_weight).toLocaleString('en', { maximumFractionDigits: 1 })}t`
                                            : '-'}
                                        </td>
                                        <td className="py-1.5 text-right tabular-nums text-gray-500">
                                          {t.receipt_weight
                                            ? `${parseFloat(t.receipt_weight).toLocaleString('en', { maximumFractionDigits: 1 })}t`
                                            : '-'}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            )
          ) : /* ============ ALL PRELOADS VIEW ============ */
          loadingAllPreloads ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-gray-200 border-t-primary-500" />
            </div>
          ) : filteredAllPreloads.length === 0 ? (
            <div className="text-center py-12">
              <CheckCircle className="w-6 h-6 mx-auto mb-2 text-primary-500 opacity-30" />
              <p className="text-xs text-gray-400">No preloads detected in this range.</p>
            </div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-gray-50 z-10">
                <tr className="border-b border-gray-200">
                  {[
                    'Receipt Date',
                    'Delivery No',
                    'Reg No',
                    'Dispatched',
                    'Received',
                    'Gap',
                    'Product',
                    'D. Weight',
                    'R. Weight',
                  ].map((h, i) => (
                    <th
                      key={h}
                      className={`px-3 py-2 text-[10px] font-semibold text-gray-400 uppercase tracking-wider ${i === 5 || i >= 7 ? 'text-right' : 'text-left'}`}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredAllPreloads.map((t, idx) => {
                  // Group header: show date separator when date changes
                  const prevDate = idx > 0 ? filteredAllPreloads[idx - 1].result_date : null;
                  const showDateHeader =
                    !prevDate ||
                    new Date(t.result_date).toDateString() !== new Date(prevDate).toDateString();

                  return (
                    <React.Fragment key={t.preload_ticket_id}>
                      {showDateHeader && (
                        <tr className="bg-gray-50/60">
                          <td colSpan={9} className="px-3 py-1.5">
                            <span className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider">
                              {formatDate(t.result_date)}
                            </span>
                          </td>
                        </tr>
                      )}
                      <tr className="border-b border-gray-50 hover:bg-red-50/30 transition-colors">
                        <td className="px-3 py-2 text-slate-800 font-medium">
                          {formatDate(t.result_date)}
                        </td>
                        <td className="px-3 py-2 text-slate-800 font-medium">{t.delivery_no}</td>
                        <td className="px-3 py-2 text-gray-500">{t.reg_no || '-'}</td>
                        <td className="px-3 py-2 text-gray-500">
                          {new Date(t.dispatch_timestamp).toLocaleString('en-ZA', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="px-3 py-2 text-gray-500">
                          {new Date(t.receipt_timestamp).toLocaleString('en-ZA', {
                            day: '2-digit',
                            month: 'short',
                            hour: '2-digit',
                            minute: '2-digit',
                          })}
                        </td>
                        <td className="px-3 py-2 text-right font-bold text-red-600">
                          {formatGap(t.gap_minutes)}
                        </td>
                        <td
                          className="px-3 py-2 text-gray-400 max-w-[120px] truncate"
                          title={t.product}
                        >
                          {t.product || '-'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                          {t.dispatch_weight
                            ? `${parseFloat(t.dispatch_weight).toLocaleString('en', { maximumFractionDigits: 1 })}t`
                            : '-'}
                        </td>
                        <td className="px-3 py-2 text-right tabular-nums text-gray-500">
                          {t.receipt_weight
                            ? `${parseFloat(t.receipt_weight).toLocaleString('en', { maximumFractionDigits: 1 })}t`
                            : '-'}
                        </td>
                      </tr>
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {showAckModal && (
        <PreloadAcknowledgeModal
          result={showAckModal}
          monitor={monitor}
          onClose={() => setShowAckModal(null)}
          onSaved={handleAckSaved}
        />
      )}
      {showAlertLog && (
        <PreloadAlertLogModal monitor={monitor} onClose={() => setShowAlertLog(false)} />
      )}
    </div>
  );
};

export default PreloadDetailModal;
